import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMxmPosts } from '@/hooks';
import { transformPostToSuggestedItem, SuggestedItem } from '@/features/marketing';
import { useToast } from '@/contexts/ToastContext';
import { getErrorMessage } from '@/utils/error';

interface UseSuggestedContentProps {
  effectiveOrgId?: number;
  effectiveUserId?: string;
  enabled: boolean;
  fetchData?: boolean; // Whether to actually fetch data (defaults to enabled)
}

/**
 * Hook to manage suggested content tab state and operations
 */
export const useSuggestedContent = ({
  effectiveOrgId,
  effectiveUserId,
  enabled,
  fetchData = enabled,
}: UseSuggestedContentProps) => {
  const { addToast } = useToast();
  const [modalItem, setModalItem] = useState<SuggestedItem | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'draft' | 'published' | 'all'>('all');
  const [totalPosts, setTotalPosts] = useState(0);
  // Default sort: createdAt DESC (matches backend default)
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([
    { id: 'createdAt', desc: true }
  ]);

  const {
    posts,
    loading: postsLoading,
    error: postsError,
    total: postsTotal,
    fetchPost,
    updatePost,
    removePost,
    fetchPosts,
  } = useMxmPosts(effectiveOrgId, enabled && !!(effectiveOrgId || effectiveUserId), effectiveUserId, false);
  // Note: enabled controls hook initialization (for updatePost availability)
  // fetchData controls whether we actually fetch posts

  // Transform data
  const suggestedItems = useMemo((): SuggestedItem[] => {
    return posts.map(transformPostToSuggestedItem);
  }, [posts]);

  // Update total when posts total changes
  useEffect(() => {
    setTotalPosts(postsTotal);
  }, [postsTotal]);

  const mapColumnKeyToBackendField = useCallback((columnKey: string): string => {
    const mapping: Record<string, string> = {
      'id': 'id',
      'title': 'title',
      'preview': 'subtitle',
      'template': 'contentType',
      'forecast': 'scoreHint',
      'dates': 'plannedDate', // Map 'dates' column to 'plannedDate' backend field
      'plannedDate': 'plannedDate',
      'createdAt': 'createdAt',
    };
    return mapping[columnKey] || columnKey;
  }, []);

  // Fetch posts when filters change (only if fetchData is true)
  useEffect(() => {
    if (!fetchData || !(effectiveOrgId || effectiveUserId)) return;

    const offset = (currentPage - 1) * pageSize;
    const queryParams: any = {
      limit: pageSize,
      offset,
      suggested: true, // Always filter for suggested content (draft and planned posts)
      ...(searchQuery.trim() && { search: searchQuery.trim() }),
      ...(statusFilter !== 'all' && { status: statusFilter }),
    };

    // Always include sorting (use default if none specified)
    const sortItem = sorting.length > 0 ? sorting[0] : { id: 'createdAt', desc: true };
    queryParams.sortBy = mapColumnKeyToBackendField(sortItem.id);
    queryParams.sortOrder = sortItem.desc ? 'desc' : 'asc';

    fetchPosts(queryParams);
  }, [currentPage, pageSize, searchQuery, statusFilter, sorting, effectiveOrgId, effectiveUserId, fetchPosts, mapColumnKeyToBackendField, fetchData]);

  // Refetch posts with current filters
  const handleRefetchPosts = useCallback(async () => {
    const offset = (currentPage - 1) * pageSize;
    const queryParams: any = {
      limit: pageSize,
      offset,
      suggested: true, // Always filter for suggested content (draft and planned posts)
      ...(searchQuery.trim() && { search: searchQuery.trim() }),
      ...(statusFilter !== 'all' && { status: statusFilter }),
    };
    // Always include sorting (use default if none specified)
    const sortItem = sorting.length > 0 ? sorting[0] : { id: 'createdAt', desc: true };
    queryParams.sortBy = mapColumnKeyToBackendField(sortItem.id);
    queryParams.sortOrder = sortItem.desc ? 'desc' : 'asc';
    await fetchPosts(queryParams);
  }, [currentPage, pageSize, searchQuery, statusFilter, sorting, fetchPosts, mapColumnKeyToBackendField]);

  // Handle pagination change
  const handlePaginationChange = useCallback(({ pageIndex, pageSize: nextSize }: { pageIndex: number; pageSize: number }) => {
    const sizeChanged = nextSize !== pageSize;
    setPageSize(nextSize);
    setCurrentPage(sizeChanged ? 1 : pageIndex + 1);
  }, [pageSize]);

  // Handle global search
  const handleGlobalSearch = useCallback(async (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on search
  }, []);

  const handleSortingChange = useCallback((newSorting: Array<{ id: string; desc: boolean }>) => {
    setSorting(newSorting);
    setCurrentPage(1);
  }, []);

  const handleStatusFilterChange = useCallback((status: 'draft' | 'published' | 'all') => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page on filter change
  }, []);

  const handleViewItem = useCallback(async (item: SuggestedItem) => {
    if (!item.id) return;
    try {
      const fullPost = await fetchPost(item.id);
      const transformed = transformPostToSuggestedItem(fullPost);
      setModalItem(transformed);
    } catch (err) {
      console.error('Failed to fetch post details:', err);
      // Fallback to showing what we have
      setModalItem(item);
    }
  }, [fetchPost]);

  const handleRemove = useCallback(async (item: SuggestedItem) => {
    if (!item.id) return;
    try {
      await removePost(item.id);
      // Refetch posts to update pagination
      const offset = (currentPage - 1) * pageSize;
      const queryParams: any = {
        limit: pageSize,
        offset,
        suggested: true, // Always filter for suggested content
        ...(searchQuery.trim() && { search: searchQuery.trim() }),
        ...(statusFilter !== 'all' && statusFilter !== 'published' && { status: statusFilter }),
      };
      if (sorting.length > 0) {
        const sortItem = sorting[0];
        queryParams.sortBy = mapColumnKeyToBackendField(sortItem.id);
        queryParams.sortOrder = sortItem.desc ? 'desc' : 'asc';
      }
      await fetchPosts(queryParams);
    } catch (err) {
      console.error('Failed to remove post:', err);
      addToast('Failed to remove post. Please try again.', 'error');
    }
  }, [removePost, currentPage, pageSize, searchQuery, statusFilter, sorting, fetchPosts, mapColumnKeyToBackendField, addToast]);

  const handleUpdatePost = useCallback(async (item: SuggestedItem, updates: Partial<SuggestedItem>) => {
    if (!item.id) return;
    try {
      const updateDto: any = {};
      if (updates.title !== undefined) updateDto.title = updates.title;
      if (updates.preview !== undefined) updateDto.subtitle = updates.preview;
      if (updates.template !== undefined) updateDto.contentType = updates.template;

      await updatePost(item.id, updateDto);
      // Refetch posts to ensure data is in sync with server
      const offset = (currentPage - 1) * pageSize;
      const queryParams: any = {
        limit: pageSize,
        offset,
        suggested: true, // Always filter for suggested content
        ...(searchQuery.trim() && { search: searchQuery.trim() }),
        ...(statusFilter !== 'all' && statusFilter !== 'published' && { status: statusFilter }),
      };
      if (sorting.length > 0) {
        const sortItem = sorting[0];
        queryParams.sortBy = mapColumnKeyToBackendField(sortItem.id);
        queryParams.sortOrder = sortItem.desc ? 'desc' : 'asc';
      }
      await fetchPosts(queryParams);
    } catch (err) {
      console.error('Failed to update post:', err);
      addToast('Failed to update post. Please try again.', 'error');
    }
  }, [updatePost, currentPage, pageSize, searchQuery, statusFilter, sorting, fetchPosts, mapColumnKeyToBackendField, addToast]);

  // Edit Modal State
  const [editingItem, setEditingItem] = useState<SuggestedItem | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Handle Close Edit Modal
  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingItem(null);
    // Optional: Clear URL params on close
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'edit') {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Handle Edit Click (from UI)
  const handleEditItem = useCallback((item: SuggestedItem) => {
    setEditingItem(item);
    setIsEditModalOpen(true);
  }, []);

  // Handle Save Edit (Moved from SuggestedContentTab)
  const handleSaveEdit = useCallback(async (item: SuggestedItem, updates: any) => {
    if (!item.id) {
      throw new Error('Post ID is required to update');
    }

    try {
      // Call the MXM post update API
      // Pass updates directly to updatePost to avoid dropping fields
      // The updates object comes from buildSavePayload in EditContentModal and should match the backend DTO

      await updatePost(item.id, updates);

      // Refetch posts to ensure data is in sync
      const offset = (currentPage - 1) * pageSize;
      const queryParams: any = {
        limit: pageSize,
        offset,
        suggested: true,
        ...(searchQuery.trim() && { search: searchQuery.trim() }),
        ...(statusFilter !== 'all' && statusFilter !== 'published' && { status: statusFilter }),
      };
      if (sorting.length > 0) {
        const sortItem = sorting[0];
        queryParams.sortBy = mapColumnKeyToBackendField(sortItem.id);
        queryParams.sortOrder = sortItem.desc ? 'desc' : 'asc';
      }
      await fetchPosts(queryParams);

    } catch (error: unknown) {
      console.error('Failed to update post:', error);
      throw new Error(getErrorMessage(error));
    }
  }, [updatePost, currentPage, pageSize, searchQuery, statusFilter, sorting, fetchPosts, mapColumnKeyToBackendField]);


  const handleCloseModal = useCallback(() => {
    setModalItem(null);
  }, []);

  // Handle Deep Linking (action=edit&postId=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const postId = params.get('postId');

    if (action === 'edit' && postId && !editingItem) {
      // Avoid infinite loop or double fetch
      fetchPost(Number(postId))
        .then((fullPost) => {
          if (fullPost) {
            const transformed = transformPostToSuggestedItem(fullPost);
            setEditingItem(transformed);
            setIsEditModalOpen(true);

            // We do NOT clear params here immediately to avoid losing context if user refresh.
            // But we CAN clear on close.
          }
        })
        .catch((err) => {
          console.error('Failed to open deep-linked post:', err);
          addToast('Failed to load the requested post.', 'error');
        });
    }
  }, [fetchPost, addToast]); // modalItem excluded

  return {
    suggestedItems,
    postsLoading,
    postsError,
    modalItem,
    pagination: {
      pageIndex: currentPage - 1,
      pageSize,
      totalCount: totalPosts,
    },
    searchQuery,
    statusFilter,
    sorting,
    handleViewItem,
    handleRemove,
    handleUpdatePost,
    handleCloseModal,
    handleRefetchPosts,
    handlePaginationChange,
    handleGlobalSearch,
    handleStatusFilterChange,
    handleSortingChange,
    handleEditItem, // New
    handleSaveEdit, // New
    handleCloseEditModal, // New
    editingItem, // New
    isEditModalOpen, // New
    updatePost, // Export for use in calendar updates
  };
};

